import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { WorkspaceTab, WorkspaceTabId } from "./Workspace.controller";
import { computeNavOverflow } from "./workspaceNav.utils";

export interface UseWorkspaceNavControllerProps {
  tabs: ReadonlyArray<WorkspaceTab>;
  onReorderTab: (source: WorkspaceTabId, target: WorkspaceTabId) => void;
  /** Retained outside the `More` menu so the current destination stays visible. */
  activeTabId?: string | null;
}

export type WorkspaceNavController = ReturnType<
  typeof useWorkspaceNavController
>;

export const useWorkspaceNavController = ({
  tabs,
  onReorderTab,
  activeTabId,
}: UseWorkspaceNavControllerProps) => {
  const tabNodes = useRef(new Map<WorkspaceTabId, HTMLElement>());
  const stripNodeRef = useRef<HTMLDivElement | null>(null);
  const overviewNodeRef = useRef<HTMLElement | null>(null);

  const [availableWidth, setAvailableWidth] = useState(0);
  const [overviewWidth, setOverviewWidth] = useState(0);
  const [measuredWidths, setMeasuredWidths] = useState<
    Readonly<Record<string, number>>
  >({});

  const [draggingTabId, setDraggingTabId] = useState<WorkspaceTabId | null>(
    null,
  );
  const [dropTargetId, setDropTargetId] = useState<WorkspaceTabId | null>(null);

  // Mirror drag state into refs so callbacks always read the latest value
  // without going stale between React batch flushes.
  const draggingTabIdRef = useRef<WorkspaceTabId | null>(null);
  const dropTargetIdRef = useRef<WorkspaceTabId | null>(null);

  /** Bounds the space the tab strip may occupy before `More` takes over. */
  const stripRef = useCallback((node: HTMLDivElement | null) => {
    stripNodeRef.current = node;
    if (node) {
      setAvailableWidth(node.clientWidth);
    }
  }, []);

  /** The pinned Overview tab, whose width the workspace tabs cannot use. */
  const registerOverviewTrigger = useCallback((node: HTMLElement | null) => {
    overviewNodeRef.current = node;
  }, []);

  const registerTabTrigger = useCallback(
    (tabId: WorkspaceTabId, node: HTMLElement | null) => {
      if (node) {
        tabNodes.current.set(tabId, node);
      } else {
        tabNodes.current.delete(tabId);
      }
    },
    [],
  );

  // Read through a ref so the measuring effect can prune stale widths without
  // depending on the tab array identity, which changes on every render.
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  // biome-ignore lint/correctness/useExhaustiveDependencies: tabs is the trigger, not a read value. Tab nodes live in a ref, so the body reads nothing reactive; re-running when the tab set changes is what re-measures widths and re-attaches the ResizeObserver to newly mounted tabs.
  useLayoutEffect(() => {
    const measure = () => {
      const strip = stripNodeRef.current;
      if (strip) {
        setAvailableWidth(strip.clientWidth);
      }
      const overview = overviewNodeRef.current;
      if (overview) {
        setOverviewWidth(overview.getBoundingClientRect().width);
      }

      const measured: Record<string, number> = {};
      for (const [tabId, node] of tabNodes.current.entries()) {
        const width = node.getBoundingClientRect().width;
        if (width > 0) {
          measured[tabId] = width;
        }
      }
      // Merge rather than replace: a tab sitting in the `More` menu is
      // unmounted, and forgetting its width would re-price it at the estimate
      // and keep it collapsed after the strip grows back.
      setMeasuredWidths((current) => {
        const next: Record<string, number> = {};
        let changed = false;
        for (const tab of tabsRef.current) {
          const width = measured[tab.id] ?? current[tab.id];
          if (width !== undefined) {
            next[tab.id] = width;
          }
          if (width !== current[tab.id]) {
            changed = true;
          }
        }
        const sameSize =
          Object.keys(next).length === Object.keys(current).length;
        return changed || !sameSize ? next : current;
      });
    };

    measure();
    window.addEventListener("resize", measure);
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    if (observer) {
      if (stripNodeRef.current) {
        observer.observe(stripNodeRef.current);
      }
      for (const node of tabNodes.current.values()) {
        observer.observe(node);
      }
    }
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [tabs]);

  const endDrag = useCallback(() => {
    draggingTabIdRef.current = null;
    dropTargetIdRef.current = null;
    setDraggingTabId(null);
    setDropTargetId(null);
  }, []);

  const dropEdge = useCallback(
    (tabId: WorkspaceTabId): "left" | "right" | null => {
      if (!draggingTabId || draggingTabId === tabId || dropTargetId !== tabId) {
        return null;
      }
      const from = tabs.findIndex((tab) => tab.id === draggingTabId);
      const to = tabs.findIndex((tab) => tab.id === tabId);
      return from < to ? "right" : "left";
    },
    [draggingTabId, dropTargetId, tabs],
  );

  const onDragStart = useCallback(
    (tabId: WorkspaceTabId, event: React.DragEvent) => {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", tabId);
      draggingTabIdRef.current = tabId;
      setDraggingTabId(tabId);
    },
    [],
  );

  const onDragEnd = endDrag;

  // Uses a ref (not state) for draggingTabId so this callback is stable and
  // always reads the latest value, even when called inside the same
  // synchronous batch as onDragStart.
  const onDragOver = useCallback(
    (tabId: WorkspaceTabId, event: React.DragEvent) => {
      const currentDragging = draggingTabIdRef.current;
      if (!currentDragging || currentDragging === tabId) {
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      dropTargetIdRef.current = tabId;
      setDropTargetId(tabId);
    },
    [],
  );

  const onDragLeave = useCallback((tabId: WorkspaceTabId) => {
    setDropTargetId((current) => {
      if (current === tabId) {
        dropTargetIdRef.current = null;
        return null;
      }
      return current;
    });
  }, []);

  const onDrop = useCallback(
    (tabId: WorkspaceTabId, event: React.DragEvent) => {
      event.preventDefault();
      const sourceTabId = draggingTabIdRef.current;
      endDrag();
      if (sourceTabId && sourceTabId !== tabId) {
        onReorderTab(sourceTabId, tabId);
      }
    },
    [endDrag, onReorderTab],
  );

  const { visibleTabs, overflowTabs } = useMemo(
    () =>
      computeNavOverflow({
        tabs,
        availableWidth,
        measuredWidths,
        reservedWidth: overviewWidth,
        activeTabId,
      }),
    [tabs, availableWidth, measuredWidths, overviewWidth, activeTabId],
  );

  // Overflow unmounts the native drag source before `dragend` can fire, so
  // clear controller state when the dragged tab leaves the visible set.
  useLayoutEffect(() => {
    const dragging = draggingTabIdRef.current;
    if (dragging && !visibleTabs.some((tab) => tab.id === dragging)) {
      endDrag();
      return;
    }
    const dropTarget = dropTargetIdRef.current;
    if (dropTarget && !visibleTabs.some((tab) => tab.id === dropTarget)) {
      dropTargetIdRef.current = null;
      setDropTargetId(null);
    }
  }, [visibleTabs, endDrag]);

  return {
    draggingTabId,
    stripRef,
    registerOverviewTrigger,
    registerTabTrigger,
    dropEdge,
    visibleTabs,
    overflowTabs,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDragLeave,
    onDrop,
  };
};
